import React, {
    useLayoutEffect, FC, useState, CSSProperties, useRef, ReactElement
} from "react";
import { CSSTransition } from "react-transition-group";
import { isBrowser } from "@montana/helpers/browser.helper";
import { Timing } from "@montana/constants/animation";

interface IChildrenProps {
    handleButtonClick: () => void;
    ariaOpen?: boolean;
    triggerID: string;
    contentID: string;
}

export interface IAccordionProps {
    header?: ReactElement | ((props: IChildrenProps) => ReactElement);
    className?: string;
    initAsOpen?: boolean;
    onToggle?: (isOpen: boolean) => void;
    animated?: boolean;
    onClickCallback?: (val: number) => void;
    children?: JSX.Element;
    id?: string;
}

/**
 * @description
 * Wraps content (children) in an accordion.
 *
 * @example
 *  <Accordion header={<header>Accordion header</header>}>
 *      <div>Accordion body!</div>
 *  </Accordion>
 */

export const Accordion: FC<IAccordionProps> = (props: IAccordionProps): JSX.Element => {
    const {
        animated = true,
        children,
        className = "",
        header,
        initAsOpen = false,
        onToggle,
        onClickCallback,
        id,
    } = props;
    const [isOpen, setOpen] = useState<boolean>(initAsOpen);
    const [contentHeight, setContentHeight] = useState<CSSProperties>();
    const [transitionDuration, setTransitionDuration] = useState<number>();
    const [ariaOpen, setAriaOpen] = useState<boolean>();
    const contentRef = useRef<HTMLDivElement>(null);

    const useDynamicEffect = isBrowser ? useLayoutEffect : (): void => {/* Do nothing. */};

    let unmounted = false;

    const triggerID = `trigger_${id}`;
    const contentID = `content_${id}`;

    function calcHeight(): void {
        const content = contentRef.current;

        if (!content || !isBrowser) {
            return;
        }

        let asyncRunner: (cb: () => void) => void;

        if (window.requestAnimationFrame) {
            asyncRunner = window.requestAnimationFrame;
        } else {
            asyncRunner = window.setTimeout;
        }

        asyncRunner(() => {
            if (unmounted) {
                return;
            }

            const duration = Timing.STANDARD * (content.offsetHeight / 250);

            setContentHeight({
                height: isOpen ? `${content.offsetHeight}px` : 0,
                transitionDuration: `${duration}ms`,
            });

            setTransitionDuration(duration);
        });
    }

    function handleAria(): void {
        setAriaOpen(isOpen);
    }

    useDynamicEffect(() => {
        calcHeight();

        if (typeof onToggle === "function") {
            onToggle(isOpen);
        }
    }, [isOpen]);

    useDynamicEffect(() => {
        calcHeight();

        return (): void => { unmounted = true; };
    }, [children]);

    const openClasses: string = isOpen
        ? ["accordion", ...className.split(" ").filter((s) => s !== "")].map((s) => `${s}--open`).join(" ")
        : "";


    const handleButtonClick = (): void => {
        setOpen(!isOpen);
        if (onClickCallback) { onClickCallback(isOpen ? 1 : -1); }
    };

    return (
        <div className={`accordion ${animated ? "accordion--animated" : ""} ${className} ${openClasses}`}>
            <div className="accordion__header">
                {typeof header === "function"
                    ? header({ handleButtonClick, ariaOpen, triggerID, contentID })
                    : (
                        <button
                            type="button"
                            className="accordion__button"
                            id={triggerID}
                            aria-controls={contentID}
                            aria-expanded={!ariaOpen}
                            onClick={handleButtonClick}
                        >
                            {header}
                        </button>
                    )}
            </div>
            <CSSTransition
                in={isOpen}
                timeout={{
                    appear: transitionDuration,
                    enter: transitionDuration,
                    exit: transitionDuration,
                }}
                appear
                onEnter={handleAria}
                onExited={handleAria}
                style={contentHeight}
            >
                <div
                    className="accordion__content-wrapper"
                    id={contentID}
                    aria-labelledby={triggerID}
                >
                    <div ref={contentRef} className="accordion__content">
                        {children}
                    </div>
                </div>
            </CSSTransition>
        </div>
    );

};
